import { useCallback, useEffect, useRef } from 'react';
import {
  createTopLayerHost,
  hideTopLayerHost,
  raiseTopLayerHost,
} from '@ardoq/dom-utils';

export const POPUP_HOST_TAG = 'ag-popup-host';

/**
 * AG Grid renders context menus, column menus and popup cell editors into
 * `popupParent`, which defaults to the grid root. The browser's top layer —
 * where modals and the `FloatingActionBar` live — paints above any `z-index`,
 * so a grid popup can only outrank those by being hosted in the top layer too.
 *
 * The UA popover box is neutralised so the host spans the viewport: AG Grid
 * positions popups from `popupParent.getBoundingClientRect()`, and a
 * viewport-sized parent keeps that maths identical to the default.
 * `pointer-events: none` keeps the host out of hit testing so clicks outside a
 * popup still reach the page and dismiss it; the host stylesheet opts the
 * popups themselves back in.
 */
const createHost = (): HTMLElement => createTopLayerHost(POPUP_HOST_TAG);

const POPUP_HOST_STYLE_ID = 'ag-popup-host-styles';

const ensureHostStyles = (): void => {
  if (document.getElementById(POPUP_HOST_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = POPUP_HOST_STYLE_ID;
  style.textContent = `${POPUP_HOST_TAG} > .ag-popup { pointer-events: auto; }`;
  document.head.appendChild(style);
};

type TopLayerPopupParent = {
  popupHost: HTMLElement;
  popupHostGridProps: {
    popupParent: HTMLElement;
    postProcessPopup: VoidFunction;
  };
};

export const useTopLayerPopupParent = (): TopLayerPopupParent => {
  const hostRef = useRef<HTMLElement | null>(null);
  if (!hostRef.current) {
    hostRef.current = createHost();
  }
  const popupHost = hostRef.current;

  useEffect(() => {
    ensureHostStyles();
    document.body.appendChild(popupHost);
    popupHost.showPopover();
    return () => {
      hideTopLayerHost(popupHost);
      popupHost.remove();
    };
  }, [popupHost]);

  // Paint order in the top layer is the order elements were shown in, so the
  // host is re-shown whenever a popup opens to get above anything that entered
  // the layer since. `postProcessPopup` runs while the popup is attached but
  // before it takes focus. Focus already inside the host means the popup is a
  // submenu of one that is on top anyway, and re-showing would drop that focus.
  const raiseHost = useCallback(() => {
    if (popupHost.contains(document.activeElement)) {
      return;
    }
    raiseTopLayerHost(popupHost);
  }, [popupHost]);

  return {
    popupHost,
    popupHostGridProps: {
      popupParent: popupHost,
      postProcessPopup: raiseHost,
    },
  };
};
